import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/helpers';
import { t } from '../../i18n';
import { Spinner } from './Spinner';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
}

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

interface DataTableProps<T> {
  data: T[] | undefined;
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  isLoading?: boolean;
  isFetching?: boolean;
  emptyMessage?: ReactNode;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  pagination?: PaginationProps;
  footer?: ReactNode;
}

export function DataTable<T>({
  data,
  columns,
  rowKey,
  isLoading,
  isFetching,
  emptyMessage = t('Žiadne záznamy'),
  onRowClick,
  rowClassName,
  pagination,
  footer,
}: DataTableProps<T>) {
  const rows = data ?? [];
  return (
    <div className={cn('relative', isFetching && !isLoading && 'opacity-70 transition-opacity')}>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} scope="col" className={col.headerClassName}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="py-10 text-center text-secondary-500">
                  <span className="inline-flex items-center gap-2">
                    <Spinner className="text-primary-600" /> {t('Načítava sa…')}
                  </span>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-10 text-center text-secondary-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn('transition-colors hover:bg-secondary-50', onRowClick && 'cursor-pointer', rowClassName?.(row))}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={col.className}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {footer && <tfoot>{footer}</tfoot>}
        </table>
      </div>
      {pagination && pagination.total > 0 && <Pagination {...pagination} />}
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange }: PaginationProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-secondary-100 px-4 py-3 text-sm">
      <span className="text-secondary-500">
        {t('{from}–{to} z {total}', { from, to, total })}
      </span>
      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-lg border border-secondary-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            aria-label={t('Počet záznamov na stránku')}
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {t('{size} / stránka', { size })}
              </option>
            ))}
          </select>
        )}
        <button className="icon-btn" onClick={() => onPageChange(page - 1)} disabled={page <= 1} aria-label={t('Predchádzajúca strana')}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="tabular-nums text-secondary-600">
          {page} / {pages}
        </span>
        <button className="icon-btn" onClick={() => onPageChange(page + 1)} disabled={page >= pages} aria-label={t('Ďalšia strana')}>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
